import type {
  RepositoryIntelligenceSettings,
  UpdateRepositoryIntelligenceSettingsRequest,
} from '@ashniva/types';
import { Alert, Button, Card, Switch } from '@ashniva/ui';
import { useState } from 'react';

import { formatDateTime } from '../../../shared/lib/format';
import { useReauth } from '../../auth/reauth';
import { useSaveRiSettings } from '../api';
import { ProblemNotice } from './ProblemNotice';

type Values = UpdateRepositoryIntelligenceSettingsRequest;

/**
 * The organization's switches (P14-01). Each is enforced by the API on every request; saving asks for the password
 * again and is audited with the switches before and after.
 */
export function SettingsForm({ settings }: { settings: RepositoryIntelligenceSettings }) {
  const [values, setValues] = useState<Values>({
    enabled: settings.enabled,
    askEnabled: settings.askEnabled,
    analyzeEnabled: settings.analyzeEnabled,
    strictAnswers: settings.strictAnswers,
  });
  const save = useSaveRiSettings();
  const reauth = useReauth();
  const set = (patch: Partial<Values>) => {
    save.reset();
    setValues({ ...values, ...patch });
  };

  const onSave = async () => {
    if (save.isPending || reauth.active) return;
    const token = await reauth.request().catch(() => null);
    if (!token) return;
    save.mutate({ values, headers: reauth.headers(token) });
  };

  return (
    <Card title="What is switched on">
      <div className="ri-stack">
        {!settings.serviceConfigured ? (
          <Alert tone="warning" title="This Desk server cannot reach Repository Intelligence">
            Until an operator configures it, every Repository Intelligence screen says it is not
            enabled, whatever is switched on here.
          </Alert>
        ) : null}
        <Switch
          checked={values.enabled}
          onChange={(enabled) => set({ enabled })}
          label="Repository Intelligence"
          description="Off: nobody in the organization can search, read, ask about or analyse code here, and nothing is sent to Repository Intelligence."
        />
        <Switch
          checked={values.askEnabled}
          disabled={!values.enabled}
          onChange={(askEnabled) => set({ askEnabled })}
          label="Questions about code"
          description="Answers are put together from the indexed code, with a citation for every statement."
        />
        <Switch
          checked={values.analyzeEnabled}
          disabled={!values.enabled}
          onChange={(analyzeEnabled) => set({ analyzeEnabled })}
          label="Change analysis"
          description="Risk, findings, affected files and tests for a change, and tasks proposed from them."
        />
        <Switch
          checked={values.strictAnswers}
          disabled={!values.enabled || !values.askEnabled}
          onChange={(strictAnswers) => set({ strictAnswers })}
          label="Strict answers"
          description="Show a low-confidence answer as “not enough evidence” instead of as an answer."
        />
        <Switch
          checked={false}
          disabled
          onChange={() => undefined}
          label="Generative answers"
          description="Not available: answers written by an AI model are not approved. Only evidence answers are given."
        />
        <p className="ri-muted">
          {settings.stored && settings.updatedAt
            ? `Last changed ${formatDateTime(settings.updatedAt)}${settings.updatedBy ? ` by ${settings.updatedBy.name}` : ''}.`
            : 'Nobody has changed these yet: these are the defaults.'}
        </p>
        <div>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={save.isPending}
            onClick={() => void onSave()}
          >
            Save
          </Button>
        </div>
        {save.isSuccess ? (
          <Alert tone="success">Saved. It applies from the next request.</Alert>
        ) : null}
        {save.isError ? <ProblemNotice error={save.error} size="sm" /> : null}
      </div>
      {reauth.modal}
    </Card>
  );
}
